import React, { useState, useEffect } from 'react';
import { Form, Button, Card, TimePicker, InputNumber, Input, Switch, Divider, Row, Col, message, Spin, Dropdown, Menu, Empty, Collapse } from 'antd';
import { CopyOutlined, DownOutlined, PlusOutlined, DeleteOutlined, CaretRightOutlined } from '@ant-design/icons';
import { ApiHelpers } from '../../../helpers';
import moment from 'moment';

const { TextArea } = Input;

const RestaurantBookingSettings = ({ restaurantId, shortUrl }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(true);
    const [bookingSettings, setBookingSettings] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [expandedDays, setExpandedDays] = useState([]);
    const [hasSettings, setHasSettings] = useState(false);

    const daysOfWeek = [
        { key: 'monday', label: 'Luni' },
        { key: 'tuesday', label: 'Marți' },
        { key: 'wednesday', label: 'Miercuri' },
        { key: 'thursday', label: 'Joi' },
        { key: 'friday', label: 'Vineri' },
        { key: 'saturday', label: 'Sâmbătă' },
        { key: 'sunday', label: 'Duminică' }
    ];

    // Create default settings for a new restaurant
    const createDefaultSettings = () => {
        const defaultSettings = {
            minAdvanceHours: 2,
            maxAdvanceDays: 30,
            minimumNoForAutomaticReservation: 0,
            emailAddress: "",
            introduction: "",
            rules: "",
            restaurantToken: restaurantId,
            days: {}
        };

        // Add default time slots for each day inside the days object
        daysOfWeek.forEach(day => {
            defaultSettings.days[day.key] = {
                enabled: day.key !== 'sunday', // All days enabled except Sunday by default
                timeSlots: [
                    {
                        startTime: moment('10:00', 'HH:mm'),
                        endTime: moment('22:00', 'HH:mm'),
                        maxCapacity: 50,
                        maxPartySize: 10,
                        timeSlotDuration: 120
                    }
                ]
            };
        });

        return defaultSettings;
    };


    // Add this function after createDefaultSettings or before handleSubmit
    const generateUuidsForSettings = async (settings) => {
        try {
            // Create a deep copy to avoid modifying the original
            const settingsWithIds = JSON.parse(JSON.stringify(settings));

            // First, check if we have existing tokens in the current bookingSettings
            // and transfer them to our updated settings

            // Check main settings token
            if (bookingSettings.uniqueIdentificationToken) {
                settingsWithIds.uniqueIdentificationToken = bookingSettings.uniqueIdentificationToken;
                //console.log("Using existing main settings UUID:", settingsWithIds.uniqueIdentificationToken);
            } else if (!settingsWithIds.uniqueIdentificationToken) {
                const mainUuid = await ApiHelpers.getUUID();
                settingsWithIds.uniqueIdentificationToken = mainUuid.data.uuid;
                //console.log("Generated main settings UUID:", mainUuid.data.uuid);
            }

            // Ensure days object exists
            if (!settingsWithIds.days) {
                settingsWithIds.days = {};

                // If days are at root level, move them to the days object
                daysOfWeek.forEach(day => {
                    if (settingsWithIds[day.key]) {
                        settingsWithIds.days[day.key] = settingsWithIds[day.key];
                        delete settingsWithIds[day.key];
                    }
                });
            }

            // Process each day's settings
            for (const day of daysOfWeek) {
                // Skip if day doesn't exist in settings
                if (!settingsWithIds.days[day.key]) {
                    continue;
                }

                // Look for existing UUID in current bookingSettings
                if (hasSettings &&
                    bookingSettings.days &&
                    bookingSettings.days[day.key] &&
                    bookingSettings.days[day.key].uniqueIdentificationToken) {

                    // Transfer the existing UUID
                    settingsWithIds.days[day.key].uniqueIdentificationToken =
                        bookingSettings.days[day.key].uniqueIdentificationToken;

                    console.log(`Using existing UUID for ${day.key}:`,
                        settingsWithIds.days[day.key].uniqueIdentificationToken);
                } else if (!settingsWithIds.days[day.key].uniqueIdentificationToken) {
                    // Generate new UUID if none exists
                    const dayUuid = await ApiHelpers.getUUID();
                    settingsWithIds.days[day.key].uniqueIdentificationToken = dayUuid.data.uuid;
                    console.log(`Generated UUID for ${day.key}:`, dayUuid.data.uuid);
                }

                // Skip if no time slots
                if (!settingsWithIds.days[day.key].timeSlots || !settingsWithIds.days[day.key].timeSlots.length) {
                    continue;
                }

                // Process each time slot
                for (let i = 0; i < settingsWithIds.days[day.key].timeSlots.length; i++) {
                    const slot = settingsWithIds.days[day.key].timeSlots[i];

                    // Look for a matching time slot in bookingSettings
                    let existingSlotUuid = null;

                    if (hasSettings &&
                        bookingSettings.days &&
                        bookingSettings.days[day.key] &&
                        bookingSettings.days[day.key].timeSlots &&
                        i < bookingSettings.days[day.key].timeSlots.length) {

                        existingSlotUuid = bookingSettings.days[day.key].timeSlots[i].uniqueIdentificationToken;
                    }

                    if (existingSlotUuid) {
                        // Transfer the existing UUID
                        settingsWithIds.days[day.key].timeSlots[i].uniqueIdentificationToken = existingSlotUuid;
                        console.log(`Using existing UUID for ${day.key} time slot ${i}:`, existingSlotUuid);
                    } else if (!slot.uniqueIdentificationToken) {
                        // Generate a new UUID if none exists
                        const slotUuid = await ApiHelpers.getUUID();
                        settingsWithIds.days[day.key].timeSlots[i].uniqueIdentificationToken = slotUuid.data.uuid;
                        console.log(`Generated UUID for ${day.key} time slot ${i}:`, slotUuid.data.uuid);
                    }

                    // Format time values for API
                    if (slot.startTime && moment.isMoment(slot.startTime)) {
                        settingsWithIds.days[day.key].timeSlots[i].startTime = slot.startTime.format('HH:mm');
                    }
                    else if (slot.startTime) {
                        // Just in case it's a JS Date or ISO string instead of a Moment
                        settingsWithIds.days[day.key].timeSlots[i].startTime = moment(slot.startTime).format('HH:mm');
                    }
                    if (slot.endTime && moment.isMoment(slot.endTime)) {
                        settingsWithIds.days[day.key].timeSlots[i].endTime = slot.endTime.format('HH:mm');
                    }
                    else if (slot.endTime) {
                        // Just in case it's a JS Date or ISO string instead of a Moment
                        settingsWithIds.days[day.key].timeSlots[i].endTime = moment(slot.endTime).format('HH:mm');
                    }
                }
            }

            return settingsWithIds;
        } catch (error) {
            console.error("Error generating UUIDs:", error);
            throw error;
        }
    };
    // Fetch booking settings
    useEffect(() => {
        setIsLoading(true);
        ApiHelpers.getBookingSettings(shortUrl)
            .then((response) => {
                // Check if settings exist
                if (response.data && Object.keys(response.data).length > 0) {
                    let settings = response.data;
                    setHasSettings(true);

                    // Ensure days are in the right structure
                    if (!settings.days) {
                        settings.days = {};

                        // Move any existing day settings to the days object
                        daysOfWeek.forEach(day => {
                            if (settings[day.key]) {
                                settings.days[day.key] = settings[day.key];
                                delete settings[day.key]; // Remove from root level
                            }
                        });
                    }

                    // Format time slots for form
                    daysOfWeek.forEach(day => {
                        if (settings.days[day.key]) {
                            if (settings.days[day.key].timeSlots === undefined || settings.days[day.key].timeSlots === null) {
                                // Only initialize with a default time slot if timeSlots doesn't exist
                                settings.days[day.key].timeSlots = [
                                    {
                                        startTime: moment('10:00', 'HH:mm'),
                                        endTime: moment('22:00', 'HH:mm'),
                                        maxCapacity: 50,
                                        maxPartySize: 10,
                                        timeSlotDuration: 120
                                    }
                                ];
                            } else if (settings.days[day.key].timeSlots.length > 0) {
                                // Format existing time slots
                                settings.days[day.key].timeSlots = settings.days[day.key].timeSlots.map(slot => ({
                                    ...slot,
                                    startTime: moment(slot.startTime, 'HH:mm'),
                                    endTime: moment(slot.endTime, 'HH:mm')
                                }));
                            }
                            // If timeSlots is an empty array, leave it as is
                        }
                        // ...
                    });

                    setBookingSettings(settings);
                    form.setFieldsValue(settings);
                } else {
                    // If no settings exist, create default settings
                    console.log("No booking settings found for this restaurant, creating default settings");
                    const defaultSettings = createDefaultSettings();
                    setBookingSettings(defaultSettings);
                    form.setFieldsValue(defaultSettings);
                    setHasSettings(false);
                }

                // Set first day as expanded by default
                setExpandedDays(['monday']);
                setIsLoading(false);
            })
            .catch((error) => {
                console.log("Error loading booking settings:", error);
                // Instead of showing an error, just initialize with default settings
                const defaultSettings = createDefaultSettings();
                setBookingSettings(defaultSettings);
                form.setFieldsValue(defaultSettings);
                setHasSettings(false);
                setExpandedDays(['monday']);
                setIsLoading(false);
            });
    }, [restaurantId]);

    // Handle form submission with UUID generation
    // Modified handleSubmit function to ensure all time slots are included
    const handleSubmit = (values) => {
        setIsSaving(true);

        // Create a defensive copy of the values to avoid modifying the original form data
        let formValues = JSON.parse(JSON.stringify(values));

        // Ensure we have complete data for all days by merging with bookingSettings
        // This fixes the issue where collapsed days might not have their time slots included
        if (bookingSettings && bookingSettings.days) {
            if (!formValues.days) {
                formValues.days = {};
            }

            // For each day in bookingSettings, ensure its data is properly included in formValues
            daysOfWeek.forEach(day => {
                const dayKey = day.key;

                // If the day exists in bookingSettings but is incomplete in formValues,
                // we need to ensure the time slots are properly included
                if (bookingSettings.days[dayKey] &&
                    bookingSettings.days[dayKey].timeSlots &&
                    (!formValues.days[dayKey] || !formValues.days[dayKey].timeSlots)) {

                    // Initialize the day in formValues if it doesn't exist
                    if (!formValues.days[dayKey]) {
                        formValues.days[dayKey] = {
                            enabled: bookingSettings.days[dayKey].enabled || false
                        };
                    }

                    // Copy over the time slots from bookingSettings
                    formValues.days[dayKey].timeSlots = [...bookingSettings.days[dayKey].timeSlots];

                    //console.log(`Restored time slots for ${dayKey} from bookingSettings`);
                }
            });
        }

        // Now generate UUIDs and format values for API
        generateUuidsForSettings(formValues)
            .then(formattedValues => {
                // Make sure restaurantToken is set correctly
                formattedValues.restaurantToken = restaurantId;

                // Log the data being sent to API for debugging
                // console.log("Sending to API:", JSON.stringify(formattedValues));

                // Send to API
                ApiHelpers.updateBookingSettings(formattedValues)
                    .then((response) => {
                        setHasSettings(true);
                        message.success('Setările pentru rezervări au fost salvate');

                        // If response includes updated settings, update the form
                        if (response.data && response.data.settings) {
                            let updatedSettings = response.data.settings;

                            // Format time slots for form
                            daysOfWeek.forEach(day => {
                                if (updatedSettings.days && updatedSettings.days[day.key] &&
                                    updatedSettings.days[day.key].timeSlots &&
                                    updatedSettings.days[day.key].timeSlots.length > 0) {
                                    updatedSettings.days[day.key].timeSlots = updatedSettings.days[day.key].timeSlots.map(slot => ({
                                        ...slot,
                                        startTime: moment(slot.startTime, 'HH:mm'),
                                        endTime: moment(slot.endTime, 'HH:mm')
                                    }));
                                }
                            });

                            setBookingSettings(updatedSettings);
                            form.setFieldsValue(updatedSettings);
                        }

                        setIsSaving(false);
                    })
                    .catch((error) => {
                        console.log(error);
                        message.error('Eroare la salvarea setărilor');
                        setIsSaving(false);
                    });
            })
            .catch(error => {
                console.error("Error preparing data:", error);
                message.error('Eroare la pregătirea datelor');
                setIsSaving(false);
            });
    };

    // Toggle day availability
    const handleDayToggle = (day, enabled) => {
        const currentValues = form.getFieldsValue();

        // Ensure days structure exists
        if (!currentValues.days) {
            currentValues.days = {};
        }

        if (enabled) {
            // Initialize day settings if enabling
            if (!currentValues.days[day]) {
                // Generate UUID for the day and its initial time slot
                ApiHelpers.getUUID()
                    .then(dayResponse => {
                        const dayUuid = dayResponse.data.uuid;

                        ApiHelpers.getUUID()
                            .then(slotResponse => {
                                const slotUuid = slotResponse.data.uuid;

                                // Update form with proper structure and UUIDs
                                const updatedValues = { ...currentValues };
                                updatedValues.days[day] = {
                                    enabled: true,
                                    uniqueIdentificationToken: dayUuid,
                                    timeSlots: [
                                        {
                                            uniqueIdentificationToken: slotUuid,
                                            startTime: moment('10:00', 'HH:mm'),
                                            endTime: moment('22:00', 'HH:mm'),
                                            maxCapacity: 50,
                                            maxPartySize: 10,
                                            timeSlotDuration: 120
                                        }
                                    ]
                                };

                                form.setFieldsValue(updatedValues);

                                // Update local state for UI consistency
                                const updatedSettings = { ...bookingSettings };
                                if (!updatedSettings.days) {
                                    updatedSettings.days = {};
                                }
                                updatedSettings.days[day] = updatedValues.days[day];
                                setBookingSettings(updatedSettings);

                                // Expand the day when it's enabled
                                if (!expandedDays.includes(day)) {
                                    setExpandedDays([...expandedDays, day]);
                                }
                            })
                            .catch(error => {
                                console.error("Error generating UUID for time slot:", error);
                                message.error("Eroare la generarea identificatorului pentru intervalul orar");
                            });
                    })
                    .catch(error => {
                        console.error("Error generating UUID for day:", error);
                        message.error("Eroare la generarea identificatorului pentru zi");
                    });
            } else {
                // Update existing day settings
                const updatedValues = { ...currentValues };
                if (!updatedValues.days[day]) {
                    updatedValues.days[day] = {};
                }
                updatedValues.days[day].enabled = true;

                form.setFieldsValue(updatedValues);

                // Expand the day when it's enabled
                if (!expandedDays.includes(day)) {
                    setExpandedDays([...expandedDays, day]);
                }
            }
        } else {
            // Update enabled state if disabling
            const updatedValues = { ...currentValues };
            if (!updatedValues.days) {
                updatedValues.days = {};
            }
            if (!updatedValues.days[day]) {
                updatedValues.days[day] = {};
            }
            updatedValues.days[day].enabled = false;

            form.setFieldsValue(updatedValues);
        }
    };

    // Add a new time slot to a day
    const addTimeSlot = (day) => {
        const currentValues = form.getFieldsValue();

        // Ensure days structure exists
        if (!currentValues.days) {
            currentValues.days = {};
        }

        // Ensure day settings exist
        if (!currentValues.days[day]) {
            currentValues.days[day] = {
                enabled: true,
                timeSlots: []
            };
        }

        // Ensure timeSlots array exists
        if (!currentValues.days[day].timeSlots || !Array.isArray(currentValues.days[day].timeSlots)) {
            currentValues.days[day].timeSlots = [];
        }

        const daySettings = currentValues.days[day];

        // Calculate new time slot times
        let newStartTime, newEndTime;

        if (daySettings.timeSlots.length > 0) {
            // Get end time of last slot to use as start time for new slot
            const lastSlot = daySettings.timeSlots[daySettings.timeSlots.length - 1];
            newStartTime = lastSlot.endTime ? moment(lastSlot.endTime) : moment('22:00', 'HH:mm');
            newEndTime = moment(newStartTime).add(2, 'hours');
        } else {
            // Default values if no existing slots
            newStartTime = moment('10:00', 'HH:mm');
            newEndTime = moment('22:00', 'HH:mm');
        }

        // Generate UUID for new time slot
        ApiHelpers.getUUID()
            .then(response => {
                const uuid = response.data.uuid;

                // Add the new time slot with UUID
                const newSlot = {
                    uniqueIdentificationToken: uuid,
                    startTime: newStartTime,
                    endTime: newEndTime,
                    maxCapacity: daySettings.timeSlots.length > 0 ? daySettings.timeSlots[0].maxCapacity || 50 : 50,
                    maxPartySize: daySettings.timeSlots.length > 0 ? daySettings.timeSlots[0].maxPartySize || 10 : 10,
                    timeSlotDuration: daySettings.timeSlots.length > 0 ? daySettings.timeSlots[0].timeSlotDuration || 120 : 120
                };

                const updatedTimeSlots = [...daySettings.timeSlots, newSlot];

                // Update the form with the new time slot
                const updatedValues = { ...currentValues };
                updatedValues.days[day].timeSlots = updatedTimeSlots;

                form.setFieldsValue(updatedValues);

                // Force a re-render since directly modifying form values doesn't always trigger UI updates
                const updatedSettings = { ...bookingSettings };
                if (!updatedSettings.days) {
                    updatedSettings.days = {};
                }
                if (!updatedSettings.days[day]) {
                    updatedSettings.days[day] = { enabled: true, timeSlots: [] };
                }
                updatedSettings.days[day].timeSlots = updatedTimeSlots;
                setBookingSettings(updatedSettings);
            })
            .catch(error => {
                console.error("Error generating UUID for new time slot:", error);
                message.error("Eroare la generarea identificatorului pentru intervalul orar");
            });
    };

    // Remove a time slot from a day
    const removeTimeSlot = (day, index) => {
        const currentValues = form.getFieldsValue();

        // Ensure proper structure exists
        if (!currentValues.days || !currentValues.days[day] ||
            !currentValues.days[day].timeSlots ||
            currentValues.days[day].timeSlots.length <= 1) {
            message.warning('Trebuie să existe cel puțin un interval orar');
            return;
        }

        const updatedTimeSlots = [...currentValues.days[day].timeSlots];
        updatedTimeSlots.splice(index, 1);

        // Update form values
        const updatedValues = { ...currentValues };
        updatedValues.days[day].timeSlots = updatedTimeSlots;

        form.setFieldsValue(updatedValues);

        // Update state for UI refresh
        const updatedSettings = { ...bookingSettings };
        if (updatedSettings.days && updatedSettings.days[day]) {
            updatedSettings.days[day].timeSlots = updatedTimeSlots;
            setBookingSettings(updatedSettings);
        }
    };

    // Function to handle copying settings from one day to other days
    const copyDaySettings = (sourceDay, targetDays) => {
        const currentValues = form.getFieldsValue();

        // Ensure days structure exists
        if (!currentValues.days) {
            currentValues.days = {};
        }

        // Check if source day exists
        if (!currentValues.days[sourceDay]) {
            message.error(`Nu există setări pentru ${daysOfWeek.find(d => d.key === sourceDay).label}`);
            return;
        }

        const sourceSettings = currentValues.days[sourceDay];

        // Generate new UUIDs for each copied day and its time slots
        const generateAndCopySettings = async () => {
            try {
                const updatedValues = { ...currentValues };

                for (const targetDay of targetDays) {
                    // Generate new UUID for target day
                    const dayUuidResponse = await ApiHelpers.getUUID();
                    const dayUuid = dayUuidResponse.data.uuid;

                    // Before this JSON serialization, temporarily convert moment objects to strings
                    const sourceSettingsForCopy = { ...sourceSettings };
                    if (sourceSettingsForCopy.timeSlots && sourceSettingsForCopy.timeSlots.length > 0) {
                        sourceSettingsForCopy.timeSlots = sourceSettingsForCopy.timeSlots.map(slot => {
                            const slotCopy = { ...slot };

                            // Store time values as strings
                            if (slotCopy.startTime && moment.isMoment(slotCopy.startTime)) {
                                slotCopy.startTime = slotCopy.startTime.format('HH:mm');
                            }

                            if (slotCopy.endTime && moment.isMoment(slotCopy.endTime)) {
                                slotCopy.endTime = slotCopy.endTime.format('HH:mm');
                            }

                            return slotCopy;
                        });
                    }

                    // Deep copy source settings
                    const targetDaySettings = JSON.parse(JSON.stringify(sourceSettingsForCopy));

                    // After deep copying, convert the string times back to moment objects
                    if (targetDaySettings.timeSlots && targetDaySettings.timeSlots.length > 0) {
                        targetDaySettings.timeSlots = targetDaySettings.timeSlots.map(slot => {
                            const slotCopy = { ...slot };

                            // Convert string times back to moment objects
                            if (slotCopy.startTime && typeof slotCopy.startTime === 'string') {
                                slotCopy.startTime = moment(slotCopy.startTime, 'HH:mm');
                            }

                            if (slotCopy.endTime && typeof slotCopy.endTime === 'string') {
                                slotCopy.endTime = moment(slotCopy.endTime, 'HH:mm');
                            }

                            return slotCopy;
                        });
                    }

                    // Set new UUID for the target day
                    targetDaySettings.uniqueIdentificationToken = dayUuid;

                    // Generate new UUIDs for each time slot
                    if (targetDaySettings.timeSlots && targetDaySettings.timeSlots.length > 0) {
                        for (let i = 0; i < targetDaySettings.timeSlots.length; i++) {
                            // Generate new UUID for this time slot
                            const slotUuidResponse = await ApiHelpers.getUUID();
                            targetDaySettings.timeSlots[i].uniqueIdentificationToken = slotUuidResponse.data.uuid;

                            // Properly handle startTime
                            if (targetDaySettings.timeSlots[i].startTime) {
                                // Store the original time value as a string before rebuilding the moment object
                                let startTimeStr;

                                if (moment.isMoment(targetDaySettings.timeSlots[i].startTime)) {
                                    startTimeStr = targetDaySettings.timeSlots[i].startTime.format('HH:mm');
                                } else if (typeof targetDaySettings.timeSlots[i].startTime === 'string') {
                                    startTimeStr = targetDaySettings.timeSlots[i].startTime;
                                } else {
                                    startTimeStr = moment(targetDaySettings.timeSlots[i].startTime).format('HH:mm');
                                }

                                // Create a fresh moment object with the extracted time
                                targetDaySettings.timeSlots[i].startTime = moment(startTimeStr, 'HH:mm');
                            }

                            // Properly handle endTime
                            if (targetDaySettings.timeSlots[i].endTime) {
                                // Store the original time value as a string before rebuilding the moment object
                                let endTimeStr;

                                if (moment.isMoment(targetDaySettings.timeSlots[i].endTime)) {
                                    endTimeStr = targetDaySettings.timeSlots[i].endTime.format('HH:mm');
                                } else if (typeof targetDaySettings.timeSlots[i].endTime === 'string') {
                                    endTimeStr = targetDaySettings.timeSlots[i].endTime;
                                } else {
                                    endTimeStr = moment(targetDaySettings.timeSlots[i].endTime).format('HH:mm');
                                }

                                // Create a fresh moment object with the extracted time
                                targetDaySettings.timeSlots[i].endTime = moment(endTimeStr, 'HH:mm');
                            }
                        }
                    }

                    // Update values for this target day
                    updatedValues.days[targetDay] = targetDaySettings;
                }

                // Update form with all changes
                form.setFieldsValue(updatedValues);

                // Update state for UI refresh
                const updatedSettings = { ...bookingSettings };
                if (!updatedSettings.days) {
                    updatedSettings.days = {};
                }
                targetDays.forEach(targetDay => {
                    updatedSettings.days[targetDay] = updatedValues.days[targetDay];
                });
                setBookingSettings(updatedSettings);

                message.success('Setări copiate cu succes!');
            } catch (error) {
                console.error("Error copying settings:", error);
                message.error('Eroare la copierea setărilor');
            }
        };

        generateAndCopySettings();
    };

    // Function to copy from one day to all other days
    const copyToAllDays = (sourceDay) => {
        const targetDays = daysOfWeek
            .map(day => day.key)
            .filter(day => day !== sourceDay);

        copyDaySettings(sourceDay, targetDays);
    };

    // Function to copy from one day to weekdays only
    const copyToWeekdays = (sourceDay) => {
        const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
        const targetDays = weekdays.filter(day => day !== sourceDay);

        copyDaySettings(sourceDay, targetDays);
    };

    // Function to copy from one day to weekend only
    const copyToWeekend = (sourceDay) => {
        const weekend = ['saturday', 'sunday'];
        const targetDays = weekend.filter(day => day !== sourceDay);

        copyDaySettings(sourceDay, targetDays);
    };

    // Handle collapse/expand of days
    const onCollapseChange = (keys) => {
        setExpandedDays(keys);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="booking-settings-container">

            {!hasSettings && (
                <div className="alert-message" style={{ marginBottom: '20px', padding: '15px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px' }}>
                    <strong>Notă:</strong> Nu există încă setări de rezervare pentru acest restaurant.
                    Completați formularul de mai jos și apăsați "Salvează setări" pentru a configura
                    disponibilitatea rezervărilor.
                </div>
            )}
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                size="large"
                initialValues={bookingSettings}
            >
                <Row gutter={24}>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Email pentru notificări rezervări"
                            name="emailAddress"
                            rules={[
                                {
                                    required: true,
                                    type: 'email',
                                    message: 'Adresa de email nu este validă!'
                                }
                            ]}
                            tooltip="Adresa de email la care veți primi notificări când sunt create rezervări noi"
                        >
                            <Input placeholder="exemplu@domeniu.com" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Timp minim înainte de rezervare (ore)"
                            name="minAdvanceHours"
                            rules={[{ required: true, message: 'Câmp obligatoriu' }]}
                        >
                            <InputNumber min={0} max={72} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Timp maxim înainte de rezervare (zile)"
                            name="maxAdvanceDays"
                            rules={[{ required: true, message: 'Câmp obligatoriu' }]}
                        >
                            <InputNumber min={1} max={90} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Număr maxim persoane pentru confirmare automată"
                            name="minimumNoForAutomaticReservation"
                            rules={[{ required: false, message: 'Câmp obligatoriu' }]}
                            tooltip="Rezervările pentru un număr de persoane mai mare decât această valoare vor necesita aprobare manuală"
                        >
                            <InputNumber
                                min={0}
                                max={50}
                                style={{ width: '100%' }}
                                placeholder=""
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Mesaj de introducere"
                            name="introduction"
                            tooltip="Un mesaj de introducere care va fi afișat clienților atunci când fac o rezervare"
                        >
                            <TextArea
                                rows={4}
                                placeholder="Introduceți un text de bun venit care să fie afișat clienților (opțional)"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Reguli de rezervare"
                            name="rules"
                            tooltip="Regulile de rezervare care vor fi afișate clienților înainte de a finaliza rezervarea"
                        >
                            <TextArea
                                rows={4}
                                placeholder="Introduceți regulile de rezervare (de exemplu: politica de anulare, reguli speciale etc.)"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider orientation="left">Program rezervări pe zile</Divider>
                <div className="settings-tip" style={{ marginBottom: '15px' }}>
                    Configurați programul de rezervări pentru fiecare zi. Puteți adăuga mai multe intervale orare pentru fiecare zi (ex: 10:00-14:00 și 18:00-22:00) și puteți copia setările de la o zi la alta folosind butonul <strong>Copiază</strong>.
                </div>
                <Collapse
                    activeKey={expandedDays}
                    onChange={onCollapseChange}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className="days-collapse"
                >
                    {daysOfWeek.map(day => (
                        <Collapse.Panel
                            key={day.key}
                            header={
                                <div className="day-card-header">
                                    <span>{day.label}</span>
                                    <Form.Item
                                        name={['days', day.key, 'enabled']}
                                        valuePropName="checked"
                                        noStyle
                                    >
                                        <Switch
                                            checkedChildren="Deschis"
                                            unCheckedChildren="Închis"
                                            onChange={(checked) => handleDayToggle(day.key, checked)}
                                            onClick={(e) => {
                                                // Make sure to properly stop propagation
                                                if (e && e.stopPropagation) {
                                                    e.stopPropagation();
                                                }
                                                return false; // Also return false to prevent default
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                            }
                            extra={
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            <Menu.Item key="all" onClick={() => copyToAllDays(day.key)}>
                                                Copiază la toate zilele
                                            </Menu.Item>
                                            <Menu.Item key="weekdays" onClick={() => copyToWeekdays(day.key)}>
                                                Copiază doar la zilele lucrătoare (L-V)
                                            </Menu.Item>
                                            <Menu.Item key="weekend" onClick={() => copyToWeekend(day.key)}>
                                                Copiază doar la weekend (S-D)
                                            </Menu.Item>
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <Button
                                        type="text"
                                        icon={<CopyOutlined />}
                                        className="copy-button"
                                        onClick={(e) => e.stopPropagation && e.stopPropagation()}
                                    >
                                        Copiază <DownOutlined />
                                    </Button>
                                </Dropdown>
                            }
                            className="day-panel"
                        >
                            <Form.Item shouldUpdate>
                                {({ getFieldValue }) => {
                                    const dayEnabled = getFieldValue(['days', day.key, 'enabled']);
                                    const timeSlots = getFieldValue(['days', day.key, 'timeSlots']) || [];

                                    return (
                                        <div className={dayEnabled ? '' : 'day-disabled'}>
                                            {timeSlots.length > 0 ? (
                                                <>
                                                    {timeSlots.map((_, index) => (
                                                        <Card
                                                            key={index}
                                                            className="time-slot-card"
                                                            title={`Interval orar ${index + 1}`}
                                                            extra={
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => removeTimeSlot(day.key, index)}
                                                                    disabled={!dayEnabled || timeSlots.length <= 1}
                                                                >
                                                                    Șterge
                                                                </Button>
                                                            }
                                                        >
                                                            <Row gutter={24}>
                                                                <Col xs={24} md={12}>
                                                                    <Form.Item
                                                                        label="Ora deschidere"
                                                                        name={['days', day.key, 'timeSlots', index, 'startTime']}
                                                                        rules={[{ required: dayEnabled, message: 'Câmp obligatoriu' }]}
                                                                    >
                                                                        <TimePicker
                                                                            format="HH:mm"
                                                                            minuteStep={15}
                                                                            style={{ width: '100%' }}
                                                                            disabled={!dayEnabled}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={12}>
                                                                    <Form.Item
                                                                        label="Ora ultimei rezervări"
                                                                        name={['days', day.key, 'timeSlots', index, 'endTime']}
                                                                        rules={[{ required: dayEnabled, message: 'Câmp obligatoriu' }]}
                                                                    >
                                                                        <TimePicker
                                                                            format="HH:mm"
                                                                            minuteStep={15}
                                                                            style={{ width: '100%' }}
                                                                            disabled={!dayEnabled}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                            <Row gutter={24}>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Capacitate maximă"
                                                                        name={['days', day.key, 'timeSlots', index, 'maxCapacity']}
                                                                        rules={[{ required: dayEnabled, message: 'Câmp obligatoriu' }]}
                                                                        tooltip="Numărul maxim de persoane care pot fi rezervate în acest interval orar"
                                                                    >
                                                                        <InputNumber
                                                                            min={1}
                                                                            style={{ width: '100%' }}
                                                                            disabled={!dayEnabled}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Dimensiune maximă grup"
                                                                        name={['days', day.key, 'timeSlots', index, 'maxPartySize']}
                                                                        rules={[{ required: dayEnabled, message: 'Câmp obligatoriu' }]}
                                                                        tooltip="Numărul maxim de persoane pentru o rezervare"
                                                                    >
                                                                        <InputNumber
                                                                            min={1}
                                                                            style={{ width: '100%' }}
                                                                            disabled={!dayEnabled}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Durată slot (minute)"
                                                                        name={['days', day.key, 'timeSlots', index, 'timeSlotDuration']}
                                                                        rules={[{ required: dayEnabled, message: 'Câmp obligatoriu' }]}
                                                                        tooltip="Durata implicită a unei rezervări"
                                                                    >
                                                                        <InputNumber
                                                                            min={30}
                                                                            step={15}
                                                                            style={{ width: '100%' }}
                                                                            disabled={!dayEnabled}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    ))}

                                                    <Button
                                                        type="dashed"
                                                        className="add-slot-button"
                                                        onClick={() => addTimeSlot(day.key)}
                                                        disabled={!dayEnabled}
                                                        block
                                                        icon={<PlusOutlined />}
                                                    >
                                                        Adaugă interval orar
                                                    </Button>
                                                </>
                                            ) : dayEnabled ? (
                                                <Empty
                                                    description="Nu există intervale orare configurate"
                                                    imageStyle={{ height: 60 }}
                                                >
                                                    <Button
                                                        type="primary"
                                                        onClick={() => addTimeSlot(day.key)}
                                                        icon={<PlusOutlined />}
                                                    >
                                                        Adaugă interval orar
                                                    </Button>
                                                </Empty>
                                            ) : (
                                                <div className="disabled-message">Activați ziua pentru a configura intervalele orare</div>
                                            )}
                                        </div>
                                    );
                                }}
                            </Form.Item>
                        </Collapse.Panel>
                    ))}
                </Collapse>
                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isSaving}
                        size="large"
                    >
                        {hasSettings ? 'Salvează setări' : 'Creează setări'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default RestaurantBookingSettings;